<template>
    <div class="package-card">
        <div class="step-heading">Review and enroll</div>

        <div class="review-box mt-4">
            <div class="box-heading">Your Bundle</div>
            <div class="tier-label">{{ pkgTargetHours }} Clock Hour Bundle</div>
            <div class="focus-line" v-if="pkgFocusLine">{{ pkgFocusLine }}</div>

            <div class="items mt-3">
                <div class="item" v-for="selection in pkgSelections" :key="'review-' + selection.key">
                    <div>{{ selection.name }}</div>
                    <div class="hours">{{ selection.hours }} hrs</div>
                </div>
            </div>

            <div class="totals">
                <div class="row">
                    <div>Bundle price</div>
                    <div class="value">{{ pkgPrice | money }}</div>
                </div>
                <div class="row">
                    <div>Bundle savings</div>
                    <div class="save">&minus;{{ pkgSavings | money }} included</div>
                </div>
                <div class="row" v-if="pkgTaxIncluded > 0">
                    <div>Includes sales tax<template v-if="pkgTaxRate"> ({{ (pkgTaxRate * 100).toFixed(2) }}%<template v-if="pkgTaxJurisdiction">, {{ pkgTaxJurisdiction }}</template>)</template></div>
                    <div class="value">{{ pkgTaxIncluded | currency }}</div>
                </div>
                <div class="row">
                    <div>Payment method</div>
                    <div class="value">{{ pkgPay === 'plan' ? 'Deferred Payment Agreement' : 'Bundle cost paid today' }}</div>
                </div>
                <div class="row" v-if="pkgPay === 'plan'">
                    <div>Monthly payments</div>
                    <div class="value">{{ pkgPlan.months }} &times; {{ pkgMonthly | money }}</div>
                </div>
                <div class="row" v-if="pkgPay === 'plan'">
                    <div>First payment</div>
                    <div class="value">{{ firstPaymentDate }} &middot; {{ pkgMonthly | money }}</div>
                </div>
                <div class="row">
                    <div>Clock hour fees, as you complete</div>
                    <div class="value">{{ pkgDeferred | money }}</div>
                </div>
                <div class="row due">
                    <div>Due today</div>
                    <div>{{ dueToday }}</div>
                </div>
            </div>
        </div>

        <div class="raise-callout" v-if="pkgPay === 'plan'">
            <div class="callout-icon"><i class="fad fa-arrow-trend-up"></i></div>
            <div>
                <div class="callout-title">Earn now, pay later — your raise arrives first</div>
                <div class="callout-text">Your first payment isn't until <strong>{{ firstPaymentDate }}</strong> — October 1 is when most districts' scale-advancement raises take effect. Finish your clock hours, move up your salary schedule, and let your new pay cover the payments.</div>
                <div class="callout-note">Requires completion of your clock hours and processing by your district before a pay raise can take effect.</div>
            </div>
        </div>

        <div class="step-actions">
            <b-button type="is-text" @click="$emit('back')"><i class="fas fa-arrow-left mr-2"></i>Back</b-button>
            <b-button type="is-primary" size="is-medium" @click="$emit('finish')">
                {{ pkgPay === 'plan' ? 'Continue to Plan Setup' : 'Continue to Checkout' }}<i class="fas fa-arrow-right ml-2"></i>
            </b-button>
        </div>
    </div>
</template>

<script>
    import {mapGetters} from "vuex";
    import {firstPaymentDateFor} from "@/constants/packages";

    export default {
        name: "StepReview",
        computed: {
            ...mapGetters(['pkgTargetHours', 'pkgFocusLine', 'pkgSelections', 'pkgPrice', 'pkgUpfront', 'pkgDeferred', 'pkgSavings', 'pkgPay', 'pkgPlan', 'pkgMonthly', 'pkgTaxIncluded', 'pkgTaxRate', 'pkgTaxJurisdiction']),
            dueToday() {
                const money = this.$options.filters.money;
                // Financed bundles defer the first payment — nothing today
                if (this.pkgPay === 'plan') return money(0);
                return money(this.pkgUpfront);
            },
            // First payment is always October 1 — the raise date
            firstPaymentDate() {
                return firstPaymentDateFor(this.pkgTargetHours).format('MMMM D, YYYY');
            }
        }
    };
</script>

<style lang="scss" scoped>
    .raise-callout {
        margin-top: 1rem;
        background: #f0f7f2;
        border: 1px solid #cfe5d6;
        border-radius: 6px;
        padding: 1rem 1.2rem;
        display: flex;
        gap: 0.9rem;

        .callout-icon {
            flex-shrink: 0;

            i {
                font-size: 1.4rem;
                color: #2e7d4f;
                margin-top: 2px;
            }
        }

        .callout-title {
            font-size: 0.9rem;
            font-weight: 900;
            color: #235f3c;
        }

        .callout-text {
            font-size: 0.85rem;
            color: #666d7a;
            margin-top: 0.3rem;
        }

        .callout-note {
            font-size: 0.75rem;
            color: #7a8a9d;
            margin-top: 0.4rem;
        }
    }

    .review-box {
        border: 1px solid #e1e7ee;
        border-radius: 6px;
        padding: 1.3rem 1.5rem;

        .box-heading {
            font-size: 0.75rem;
            font-weight: bold;
            letter-spacing: 0.06em;
            text-transform: uppercase;
            color: #7a8a9d;
        }

        .tier-label {
            font-size: 1.05rem;
            font-weight: 900;
            color: #33475b;
            margin-top: 4px;
        }

        .focus-line {
            font-size: 0.85rem;
            color: #7a8a9d;
            margin-top: 2px;
        }

        .items {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;

            .item {
                display: flex;
                justify-content: space-between;
                gap: 16px;
                font-size: 0.85rem;
                color: #33475b;

                .hours {
                    color: #7a8a9d;
                    flex-shrink: 0;
                }
            }
        }

        .totals {
            border-top: 1px solid #eef1f5;
            margin-top: 1rem;
            padding-top: 0.9rem;
            display: flex;
            flex-direction: column;
            gap: 0.4rem;

            .row {
                display: flex;
                justify-content: space-between;
                font-size: 0.9rem;
                color: #666d7a;

                .value {
                    font-weight: bold;
                    color: #33475b;
                }

                .save {
                    font-weight: bold;
                    color: #2e7d4f;
                }

                &.due {
                    font-size: 1rem;
                    margin-top: 0.4rem;
                    font-weight: 900;
                    color: #33475b;

                    div:last-child {
                        color: #2e5f8f;
                    }
                }
            }
        }
    }
</style>
